import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages the free gift progress bar in the cart.
 *
 * The bar itself is rendered entirely in Liquid, so it re-renders on every cart
 * morph. This component only handles the two actions Liquid can't perform:
 * adding the gift when the threshold is reached, and dropping the gift back out
 * of the cart if the customer falls below the threshold again.
 *
 * @typedef {object} CartGiftProgressRefs
 * @property {HTMLButtonElement} [giftButton] - The "add your gift" button.
 * @extends Component<CartGiftProgressRefs>
 */
class CartGiftProgressComponent extends Component {
  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    // The cart no longer qualifies but the gift is still in it — take it back out
    // so the customer is never charged for it.
    if (this.dataset.giftInvalid === 'true') this.#removeGift();
  }

  /**
   * The section ids of every cart items component on the page, so the cart
   * re-renders in both the drawer and the cart page.
   *
   * @returns {string}
   */
  get #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  /**
   * Adds the gift variant to the cart.
   *
   * @param {Event} [event] - The click event.
   */
  async addGift(event) {
    event?.preventDefault();

    const { variantId } = this.dataset;

    if (!variantId || this.#busy) return;

    this.#setBusy(true);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.#sectionIds,
        }),
      });

      const result = await response.json();

      if (result.status) throw new Error(result.description || result.message);

      this.#celebrate();

      // `/cart/add.js` returns the added lines, not the cart. The cart icon sets
      // its badge straight from `itemCount`, so it needs the real total.
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'cart-gift-progress-component',
          itemCount: cart.item_count,
          variantId,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('Failed to add the free gift:', error);
      this.#setBusy(false);
    }
  }

  /**
   * Removes the gift line from the cart.
   */
  async #removeGift() {
    const { giftLineKey } = this.dataset;

    if (!giftLineKey || this.#busy) return;

    this.#setBusy(true);

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: giftLineKey, quantity: 0, sections: this.#sectionIds }),
      });

      const cart = await response.json();

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'cart-gift-progress-component',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error('Failed to remove the free gift:', error);
      this.#setBusy(false);
    }
  }

  /**
   * Toggles the loading state of the component.
   *
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('busy', busy);

    const { giftButton } = this.refs;
    if (giftButton) giftButton.disabled = busy;
  }

  /**
   * Fires the confetti burst that plays when the gift is claimed.
   *
   * The burst is attached outside the morph target — otherwise the cart
   * re-render would wipe it before a single frame is painted.
   */
  #celebrate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const node = this.querySelector('.gift-progress__node');
    if (!node) return;

    // A modal dialog lives in the top layer, so body-level nodes render behind it.
    const host = this.closest('dialog') ?? document.body;
    const { top, left, width, height } = node.getBoundingClientRect();

    const burst = document.createElement('span');
    burst.className = 'gift-progress__burst';
    burst.setAttribute('aria-hidden', 'true');
    burst.style.setProperty('--burst-x', `${left + width / 2}px`);
    burst.style.setProperty('--burst-y', `${top + height / 2}px`);

    for (let index = 0; index < 12; index++) {
      const piece = document.createElement('i');
      piece.style.setProperty('--angle', `${index * 30}deg`);
      piece.style.setProperty('--delay', `${index * 15}ms`);
      burst.appendChild(piece);
    }

    host.appendChild(burst);
    setTimeout(() => burst.remove(), 1200);
  }
}

if (!customElements.get('cart-gift-progress-component')) {
  customElements.define('cart-gift-progress-component', CartGiftProgressComponent);
}
